import React, { useEffect, useState, useRef, useMemo } from "react";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react-web";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

// Define types for the Buddy component
interface BuddyProps {
  id: string;
  name: string;
  description: string;
  selected: boolean;
  bgColor: string;
  onSelect: (id: string) => void;
  isCompact?: boolean;
}

// Individual Buddy Item component
const BuddyItem: React.FC<BuddyProps> = ({
  id,
  name,
  description,
  selected,
  bgColor,
  onSelect,
  isCompact = false,
}) => {
  const controls = useAnimation();

  useEffect(() => {
    controls.start({
      boxShadow: selected
        ? [
            "0 0 0px rgba(62, 166, 255, 0.3)",
            "0 0 20px rgba(62, 166, 255, 0.8)",
            "0 0 0px rgba(62, 166, 255, 0.3)",
          ]
        : "none",
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      },
    });
  }, [selected, controls]);

  // Cosmic-themed avatar for each buddy type
  const renderAvatar = () => {
    switch (id) {
      // case "default": // Commented out - using explainer as default now
      //   return "🤖";
      case "tutor":
        return "👨‍🏫";
      case "explainer":
        return "📚";
      case "solver":
        return "🧮";
      case "navigator":
        return "🧭";
      case "motivator":
        return "💪";
      case "reviewer":
        return "📝";
      case "planner":
        return "📅";
      case "blaze":
        return "👨‍🏫";
      case "live":
        return "🎬";
      case "search":
        return "🎥";
      case "learning":
        return "📝";
      // case "navigation": // Commented out - removed from UI
      //   return "🚀";
      case "settings":
        return "⚙️";
      default:
        return "📚"; // Default to explainer icon instead of robot
    }
  };

  // Always use vertical layout now
  return (
    <motion.div
      className={`flex flex-col items-center cursor-pointer transition-all duration-200 p-2 rounded-lg ${
        selected
          ? "bg-[#0a0a0a] ring-2 ring-[#3ea6ff]/70 shadow-lg shadow-[#3ea6ff]/30"
          : "hover:bg-[#0f0f0f] border border-transparent hover:border-[#3ea6ff]/30"
      }`}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(id)}
    >
      <motion.div
        className={`w-10 h-10 rounded-full flex items-center justify-center mb-1.5 ${
          selected
            ? "ring-2 ring-[#3ea6ff]/70 shadow-lg shadow-[#3ea6ff]/40"
            : ""
        }`}
        animate={{
          boxShadow: selected
            ? [
                "0 0 0 rgba(62, 166, 255, 0.5)",
                "0 0 12px rgba(62, 166, 255, 0.8)",
                "0 0 0 rgba(62, 166, 255, 0.5)",
              ]
            : "none",
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        style={{
          background: bgColor,
        }}
      >
        <span className="text-lg drop-shadow-md">{renderAvatar()}</span>
      </motion.div>

      <div className="text-center">
        <h4 className="text-[#ffffff] text-[12px] font-semibold mb-0.5 leading-tight drop-shadow-md">
          {name}
        </h4>
        <p className="text-[#d7d7d7] text-[10.5px] leading-tight">
          {description}
        </p>
      </div>
    </motion.div>
  );
};

interface BuddiesListProps {
  onSelectBuddy: (id: string) => void;
  selectedBuddyId: string | null;
  onToggleExpand?: () => void;
  isExpanded?: boolean;
}

// Separate memoized component for the cosmic background
const CosmicBackground = React.memo(() => {
  // Generate stars once and reuse them
  const stars = useMemo(
    () =>
      Array.from({ length: 15 }, (_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        opacity: Math.random() * 0.5 + 0.2,
        duration: Math.random() * 3 + 2,
        scale: Math.random() * 0.5 + 1,
      })),
    []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Stars */}
      {stars.map((star) => (
        <motion.div
          key={star.id}
          className="absolute w-1 h-1 rounded-full bg-white"
          style={{
            left: star.left,
            top: star.top,
            opacity: star.opacity,
          }}
          animate={{
            opacity: [star.opacity, star.opacity * 2, star.opacity],
            scale: [1, star.scale, 1],
          }}
          transition={{
            duration: star.duration,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Nebula effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-purple-500/5 to-transparent opacity-30 backdrop-blur-[100px]"></div>
    </div>
  );
});

CosmicBackground.displayName = "CosmicBackground";

const BuddiesList: React.FC<BuddiesListProps> = ({
  onSelectBuddy,
  selectedBuddyId,
  onToggleExpand,
  isExpanded = false,
}) => {
  const [showAll, setShowAll] = useState(false);

  const allBuddies = [
    // {
    //   id: "default", // Commented out - using explainer as default now
    //   name: "AI Helper",
    //   description: "Your friendly AI assistant",
    //   bgColor: "bg-gradient-to-br from-blue-600/80 to-indigo-700/80",
    // },
    {
      id: "explainer",
      name: "Concept Explainer",
      description: "Detailed explanations of any academic concept",
      bgColor: "bg-gradient-to-br from-cyan-500/80 to-blue-600/80",
    },
    {
      id: "learning",
      name: "Study Guide",
      description: "Step-by-step guides to master any topic",
      bgColor: "bg-gradient-to-br from-purple-500/80 to-pink-600/80",
    },
    {
      id: "search",
      name: "Video Lectures",
      description: "Watch videos explaining tough topics",
      bgColor: "bg-gradient-to-br from-blue-500/80 to-indigo-600/80",
    },
    {
      id: "blaze",
      name: "Blaze Sessions",
      description: "1-on-1 expert tutoring for any subject",
      bgColor: "bg-gradient-to-br from-orange-500/80 to-red-600/80",
    },
    {
      id: "live",
      name: "Class Finder",
      description: "Find cool live classes to join",
      bgColor: "bg-gradient-to-br from-green-500/80 to-emerald-700/80",
    },
    {
      id: "settings",
      name: "Settings Helper",
      description: "Change your theme or grade level",
      bgColor: "bg-gradient-to-br from-gray-500/80 to-gray-700/80",
    },
    // {
    //   id: "navigation", // Commented out - removed from UI
    //   name: "Quick Links",
    //   description: "Jump to different parts of PuStack easily",
    //   bgColor: "bg-gradient-to-br from-amber-500/80 to-yellow-600/80",
    // },
  ];

  // Show only 4 buddies initially since we now have 6 total
  const visibleBuddies = showAll ? allBuddies : allBuddies.slice(0, 6);
  const hiddenCount = allBuddies.length - 6;

  return (
    <div
      className="h-full flex flex-col bg-[#0a0a0a] border-r border-[#404040] overflow-hidden shadow-lg backdrop-blur-sm"
      style={{
        backgroundColor: "rgba(10, 10, 10, 0.95)", // Semi-transparent dark background
        boxShadow:
          "0 15px 40px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.08)", // Subtle rim light
      }}
    >
      {/* Header */}
      <div className="p-3 border-b border-[#404040]/50 flex-shrink-0 bg-gradient-to-b from-[#0a0a0a] to-[#161616]">
        <h3 className="text-[#ffffff] text-sm font-semibold mb-1 drop-shadow-lg">
          Learning Buddies
        </h3>
        <p className="text-[#d7d7d7] text-xs">Pick a buddy to help you!</p>
      </div>

      {/* Buddies list */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-2 min-h-0 bg-gradient-to-b from-[#0f0f0f] to-[#000000]">
        <div className="space-y-1.5">
          <AnimatePresence mode="wait">
            {visibleBuddies.map((buddy, index) => (
              <motion.div
                key={buddy.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{
                  duration: 0.2,
                  delay: showAll ? index * 0.03 : 0,
                  layout: { duration: 0.3, ease: "easeInOut" },
                }}
              >
                <BuddyItem
                  id={buddy.id}
                  name={buddy.name}
                  description={buddy.description}
                  selected={selectedBuddyId === buddy.id}
                  bgColor={buddy.bgColor}
                  onSelect={onSelectBuddy}
                  isCompact={false}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Show More/Less Button */}
        {hiddenCount > 0 && (
          <motion.div
            className="mt-3 pt-2 border-t border-[#272727]/60"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.2 }}
          >
            <motion.button
              onClick={() => setShowAll(!showAll)}
              className="w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-[#1f1f1f] hover:bg-[#272727] border border-[#3ea6ff]/30 hover:border-[#3ea6ff]/50 text-[#b3b3b3] hover:text-[#f1f1f1] text-xs font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.1 }}
            >
              {showAll ? (
                <>
                  <ChevronUpIcon className="w-4 h-4" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDownIcon className="w-4 h-4" />
                  Show {hiddenCount} More
                </>
              )}
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* Footer with expand/collapse button */}
      <div className="p-2 border-t border-[#272727] flex justify-center flex-shrink-0 bg-gradient-to-t from-[#0f0f0f] to-[#161616]">
        <button
          onClick={onToggleExpand}
          className="flex items-center text-[#b3b3b3] hover:text-[#f1f1f1] text-xs font-semibold bg-[#1f1f1f] hover:bg-[#272727] border border-[#3ea6ff]/30 hover:border-[#3ea6ff]/50 transition-all duration-200 px-3 py-2 rounded-lg shadow-md hover:shadow-lg"
        >
          <span className="mr-1.5">
            {isExpanded ? "Make Smaller" : "Make Bigger"}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {isExpanded ? (
              <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
            ) : (
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
            )}
          </svg>
        </button>
      </div>
    </div>
  );
};

export default BuddiesList;
